import { LEGAL_PERSON } from 'data-layer-cr/dist/constants/customerTypes';
import {
  Driver,
  GeneralCatalogItem,
  Quotation,
} from 'data-layer-cr/dist/models';

import { findNameByCode } from 'src/functions/mapCatatalogToDisplay';
import { cities as staticCities } from 'src/data/citiesCr.json';
import { states as staticStates } from 'src/data/statesCr.json';
import { townships as staticTownships } from 'src/data/townshipsCr.json';

import EmissionDataFields, {
  LegalBeneficiary,
} from 'src/models/helperTypes/EmissionDataFields';
import FinalBeneficiary from 'src/models/helperTypes/FinalBeneficiary';
import { EmissionClient } from '../../../EmissionRequest';

type ConvertToEmissionClientProps = {
  quotation: Quotation;
  driver: Driver;
  dataFields: EmissionDataFields;
  beneficiary: FinalBeneficiary;
  legalBeneficiaries?: LegalBeneficiary[];
  /** Optional catalog arrays for fallback name resolution when name fields are empty */
  cities?: GeneralCatalogItem[];
  townships?: GeneralCatalogItem[];
};

export default function convertToEmissionClient({
  quotation,
  driver,
  dataFields,
  beneficiary,
  legalBeneficiaries,
  cities,
  townships,
}: ConvertToEmissionClientProps): EmissionClient {
  const staticState = staticStates.find(
    (item) => item.code === dataFields.addressState,
  );
  const staticCitiesForState = staticCities.filter(
    (item) => item.parentId === staticState?.id,
  );
  const staticCity = staticCitiesForState.find(
    (item) => item.code === dataFields.addressCity,
  );
  const staticTownshipsForCity = staticTownships.filter(
    (item) => item.parentId === staticCity?.id,
  );

  const cityName =
    findNameByCode(staticCitiesForState, dataFields.addressCity) ||
    dataFields.addressCityName ||
    (cities?.length ? findNameByCode(cities, dataFields.addressCity) : '');
  const townshipName =
    findNameByCode(staticTownshipsForCity, dataFields.addressTownship) ||
    dataFields.addressTownshipName ||
    (townships?.length
      ? findNameByCode(townships, dataFields.addressTownship)
      : '');

  /**
   * These are fields that are either shared between the different customer versions,
   * or that we don't really ask for them but the request requires then nevertheless.
   */
  const sharedProperties = {
    codCli: '',
    pep: false,
    phone2: '',
    phone3: '',
    address: '',
    clientAddress: {
      detail: dataFields.addressDetail,
      country: {
        code: dataFields.addressCountry,
        name: dataFields.addressCountryName,
      },
      state: {
        code: dataFields.addressState,
        name: dataFields.addressStateName,
      },
      city: {
        code: dataFields.addressCity,
        name: cityName,
      },
      township: {
        code: dataFields.addressTownship,
        name: townshipName,
      },
    },
    // legalBeneficiaries
    legalBeneficiaries: legalBeneficiaries || [],
  };

  if (quotation.customerType === LEGAL_PERSON) {
    return {
      ...sharedProperties,

      id: dataFields.idNumber,
      idType: dataFields.idType,
      firstName: dataFields.legalEntityName,
      phone1: dataFields.phoneNumber,
      email: dataFields.email,
      nationalityId: dataFields.legalEntityCountry,
      otherNationalityId: dataFields.legalEntityCountry,

      /** Values unique to the legal entity */
      economicActivity: dataFields.economicActivity,
      economicActivityName: dataFields.economicActivityName,
      beneficiary: {
        firstName: beneficiary.firstName,
        lastName: beneficiary.lastName,
        idType: beneficiary.idType,
        id: beneficiary.id,
        birthdate: beneficiary.birthdate,
        nationalityId: beneficiary.birthPlaceId,
        nationality: beneficiary.birthPlace,
        birthPlaceId: beneficiary.birthPlaceId,
        birthPlace: beneficiary.birthPlace,
        occupation: beneficiary.occupation,
        occupationName: beneficiary.occupationName,
        companyRole: beneficiary.companyRole,
        phone1: beneficiary.phone1,
        phone2: beneficiary.phone2,
        addressDetail: beneficiary.addressDetail,
        countryId: beneficiary.countryId,
        country: beneficiary.country,
        stateId: beneficiary.stateId,
        state: beneficiary.state,
        cityId: beneficiary.cityId,
        city: beneficiary.city,
        townshipId: beneficiary.townshipId,
        township: beneficiary.township,

        /** For some reason, sometimes this value changes to string instead of a number */
        participationPercentage: Number.parseInt(
          beneficiary.sharePercentage.toString(),
          10,
        ),
      },

      socialReason: dataFields.socialReason,
      notificationPreference: dataFields.notificationPreference,
      birthdate: dataFields.birthday,
      phone2: dataFields.fixNumber,

      // shareholder
      shareholders: dataFields.shareholders,

      // stock exchange data
      isInStockExchange: dataFields.isInStockExchange,
      stockExchangeName: dataFields.stockExchangeName,
      stockExchangeCountry: dataFields.stockExchangeCountry,
      stockExchangeCountryName: dataFields.stockExchangeCountryName,
      companyType: dataFields.companyType,

      /**
       * These are empty values, required by the "natural person" type of customer
       */
      lastName: '',
      genderId: '',
      civilStatusId: '',

      profession: dataFields.profession,
      occupation: dataFields.occupation,
      occupationName: dataFields.occupationName,
      enterprise: dataFields.enterprise,
      enterpriseActivity: dataFields.enterpriseActivity,
      salary: dataFields.salary,
      typeOfMoney: dataFields.typeOfMoney,
      originCountry: dataFields.originCountry,
      originCountryName: dataFields.originCountryName,
      originDetail: dataFields.originDetail,
      originType: dataFields.originType,
      birthPlace: dataFields.birthPlaceName,
      nationality: dataFields.nationalityName,
      otherNationality: dataFields.otherNationalityName,
      civilStatus: dataFields.civilStatusName,
    };
  }

  /** Return this when the quotation is made for a natura person */
  return {
    ...sharedProperties,
    id: dataFields.idNumber,
    idType: dataFields.idType,
    genderId: dataFields.gender,
    birthdate: dataFields.birthday,
    firstName: dataFields.names,
    lastName: dataFields.lastNames,
    phone1: dataFields.phoneNumber,
    email: dataFields.email,
    nationalityId: dataFields.nationality || driver.nationality,
    otherNationalityId: dataFields.otherNationality || '',
    civilStatusId: dataFields.civilStatus || driver.civilStatus,

    profession: dataFields.profession,
    occupation: dataFields.occupation,
    occupationName: dataFields.occupationName,
    enterprise: dataFields.enterprise,
    enterpriseActivity: dataFields.enterpriseActivity,
    salary: dataFields.salary,
    typeOfMoney: dataFields.typeOfMoney,
    originCountry: dataFields.originCountry,
    originCountryName: dataFields.originCountryName,
    originDetail: dataFields.originDetail,
    originType: dataFields.originType,
    birthPlace: dataFields.birthPlaceName,
    nationality: dataFields.nationalityName,
    otherNationality: dataFields.otherNationalityName,
    civilStatus: dataFields.civilStatusName,
    economicActivity: dataFields.economicActivity,

    /**
     * These are empty values, required by the "legal person" type of customer
     */
    socialReason: '',
    notificationPreference: '',

    shareholders: [],

    isInStockExchange: false,
    stockExchangeName: '',
    stockExchangeCountry: '',
    stockExchangeCountryName: '',
    companyType: '',
  };
}
